import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrowingIcon, ShipmentsIcon, WeatherIcon } from '../../../components/icons';
import { LAYER_MODES } from '../../../utils/mapUrlParams';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { isValidView } from '../../../utils/mapViews';

const ROWS = [
  { id: LAYER_MODES.GROWING, labelKey: 'map.combine.growingRegions', fallback: 'Growing regions', Icon: GrowingIcon },
  { id: LAYER_MODES.SHIPMENTS, labelKey: 'map.layers.shipments', fallback: 'Shipments', Icon: ShipmentsIcon },
  { id: LAYER_MODES.WEATHER, labelKey: 'map.layers.weather', fallback: 'Weather', Icon: WeatherIcon },
];

// The rail icons drawn at 15px: Figma scales the same 20px glyphs by 0.75 but
// keeps a 1.1875 stroke, so the stroke is pre-divided to survive the scale.
const ROW_ICON_STROKE = 1.1875 / 0.75;

const SURFACE = floatingSurfaceClass(95);
const DIVIDER = 'border-[#EEF0EC] dark:border-[#2A302C]';
const TEXT = 'text-[#343440] dark:text-[#E6E8E6]';
const ROW = 'hover:bg-[#F3F5F2] dark:hover:bg-[#242A26]';
const CHIP = 'bg-[#F3F5F2] text-[#828382] dark:bg-[#252B27] dark:text-[#9AA09A]';
const BOX = 'border-[#D6DAD4] dark:border-[#3A403C]';
const BOX_ON = 'border-[#16A34A] bg-[#16A34A] text-white dark:border-[#22C55E] dark:bg-[#22C55E] dark:text-[#191919]';
const FOOTER = 'bg-[#FBFCFB] dark:bg-[#171A18]';
const DELETE = 'text-[#DC2626] hover:underline dark:text-[#F87171]';
const CANCEL = 'bg-white border-[#E6E9E4] text-[#828382] hover:bg-[#F3F5F2] dark:bg-[#1F2420] dark:border-[#2F3531] dark:text-[#9AA09A] dark:hover:bg-[#242A26]';
const SAVE_OFF = 'bg-[#E5E9E4] border-[#E5E9E4] text-[#9CA3AF] dark:bg-[#2A302C] dark:border-[#2A302C] dark:text-[#6B716C]';
const SAVE_ON = 'bg-[#16A34A] border-[#16A34A] text-white hover:bg-[#12b152] dark:bg-[#22C55E] dark:border-[#22C55E] dark:text-[#191919] dark:hover:bg-[#1aa84f]';

const sameSet = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * "Combine layers" popover — picks the set of layers shown together, rather
 * than the single layer the rail's radio tabs select.
 *
 * Draft state is local: the checkboxes only leave this component via
 * `onSave`, so Cancel (and a click outside, and Escape) discard cleanly.
 */
const MapCombinePopover = ({
  value = [],
  anchorRef = null,
  onClose,
  onSave,
  // Edit mode: a Delete action on the left of the footer, and a title that
  // says so. Absent when creating, which is what keeps one component serving
  // both — the rules and the rows are identical either way.
  onDelete = null,
  className = '',
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const editing = typeof onDelete === 'function';

  const [draft, setDraft] = useState(value);
  // Save needs BOTH: something changed, and the result is a view the rail can
  // hold — at least two layers and not all of them. Dirty alone would offer to
  // save a single layer, which is just that layer's own tab.
  const dirty = !sameSet(draft, value);
  const canSave = dirty && isValidView(draft);

  const toggle = useCallback((id) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  // Dismiss on Escape or on a pointer down anywhere outside. `mousedown` (not
  // click) so a press that starts on the map closes the popover before Leaflet
  // starts panning under it. The anchor counts as inside, otherwise the press
  // that closes the popover is followed by the click that reopens it.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const onDown = (e) => {
      if (anchorRef?.current?.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={editing ? t('map.combine.editTitle', 'Edit view') : t('map.combine.title', 'Combine layers')}
      className={`w-[236px] overflow-hidden rounded-[14px] border border-solid ${SURFACE} ${className}`}
    >
      <div className={`flex h-[40px] items-center border-b border-solid px-[14px] ${DIVIDER}`}>
        <span className={`text-[14px] font-extrabold ${TEXT}`}>
          {editing ? t('map.combine.editTitle', 'Edit view') : t('map.combine.title', 'Combine layers')}
        </span>
      </div>

      <div className="p-[6px]">
        {ROWS.map(({ id, labelKey, fallback, Icon }) => {
          const checked = draft.includes(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggle(id)}
              className={`flex h-[46px] w-full items-center gap-[10px] rounded-[10px] px-[9px] transition-colors ${ROW}`}
            >
              <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[8px] ${CHIP}`}>
                <Icon className="h-[15px] w-[15px]" strokeWidth={ROW_ICON_STROKE} />
              </span>
              <span className={`text-[13.5px] font-bold ${TEXT}`}>
                {t(labelKey, fallback)}
              </span>
              <span
                className={`ml-auto flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-[6px] border border-solid transition-colors ${
                  checked ? BOX_ON : BOX
                }`}
              >
                {checked && (
                  <svg viewBox="0 0 12 12" className="h-[11px] w-[11px]" fill="none" aria-hidden="true">
                    <path d="M2.5 6.2L4.8 8.5L9.5 3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className={`flex h-[51px] items-center gap-[12px] border-t border-solid pl-[14px] pr-[11px] ${DIVIDER} ${FOOTER}`}>
        {editing && (
          <button
            type="button"
            onClick={() => onDelete?.()}
            className={`mr-auto text-[12px] font-bold transition-colors ${DELETE}`}
          >
            {t('map.combine.delete', 'Delete')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onClose?.()}
          className={`${editing ? '' : 'ml-auto '}h-[30px] w-[66px] rounded-[9px] border border-solid text-[12px] font-bold transition-colors ${CANCEL}`}
        >
          {t('map.combine.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave?.(draft)}
          className={`h-[30px] w-[82px] rounded-[8px] border border-solid text-[12px] font-semibold transition-colors ${
            canSave ? SAVE_ON : `${SAVE_OFF} cursor-default`
          }`}
        >
          {t('map.combine.save', 'Save view')}
        </button>
      </div>
    </div>
  );
};

export default MapCombinePopover;
